"use client";
import React from "react";
import Header from "components/common/Header";
import Footer from "components/common/Footer";
import SectionDownloadApp from "components/common/SectionDownloadApp";
import FormProvider from "providers/FormProvider";
import useFilterForm from "hooks/useFilterForm";
import usePromise from "hooks/usePromise";
import usePagination from "hooks/usePagination";
import ShopService from "services/shop";
import WardService from "services/ward";
import { Filter } from "components/search";
import { assign, find, get } from "utils/lodash";

import { SectionVertical } from "components/home";
import { useRouteContext, useSearchParams } from "providers/RouteProvider";
import { usePageContext } from "providers/PageProvider";
import { useT } from "providers/I18nProvider";

import helper from "utils/helper";
const buildQuery = (values: any) => {
  const sort = get(values, "sort") || "";
  const input: any = helper.normalizeParams({
    where: {
      ...(get(values, "searchTerm")
        ? { name: { like: get(values, "searchTerm") } }
        : {}),
      ...(!!get(values, "ward") && {
        ward: { equals: get(values, "ward") },
      }),
      ...(!!get(values, "province") && {
        province: { equals: get(values, "province") },
      }),
      ...(!!get(values, "category", "") && {
        mainShopCategory: {
          equals: get(values, "category", ""),
        },
      }),
      // ...(get(values, "subShopCategories", []).length > 0 && {
      //   subShopCategories: {
      //     in: get(values, "subShopCategories", []),
      //   },
      // }),
    },
    page: get(values, "page"),
    limit: get(values, "limit"),
    sort,
  });
  return input;
};

const Comp = () => {
  const { pageService } = usePageContext();

  const { router } = useRouteContext();
  const t = useT();
  const searchParams = useSearchParams();
  const searchTerm = searchParams.get("searchTerm") || searchParams.get("keyword") || "";
  const category = searchParams.get("category") || "";
  const province = searchParams.get("province") || "686a7e84fc7940dcf9bb772a";

  const ward = searchParams.get("ward") || "";
  // Trang hiện tại nằm trên URL (?page=N) để F5 / chia sẻ link / Back giữ đúng trang.
  const initialPage = Math.max(parseInt(searchParams.get("page") || "1", 10) || 1, 1);
  const ref: any = React.useRef({ loading: true, first: true, initialPage });
  const pagination = usePagination({
    initialParams: {
      page: initialPage,
      limit: 16,
    },
    loader: async (payload) => {
      const categories = get(pageService, "categories");
      const categoryCode =
        payload.category ?? ref.current.form.values.category;
      const categoryId = get(
        find(categories, (item) => item.code === categoryCode),
        "id"
      );
      const query = buildQuery(
        assign({}, ref.current.form.values, payload, {
          category: categoryId,
        })
      );
      const res = await ShopService.find(query);
      return {
        data: get(res, "data", []),
        total: get(res, "params.total", 0),
      };
    },
  });
  const form: any = useFilterForm({
    enableReinitialize: true,
    initialValues: {
      searchTerm,
      province,
      ward,
      category: category,
    },
    onSubmit: async (values: any) => {
      // Lần tải đầu: mở đúng trang trên URL; các lần submit sau về trang 1.
      const page = ref.current.initialPage || 1;
      ref.current.initialPage = 1;
      ref.current.pagination.submit({ ...values, page });
    },
  });
  const [wards] = usePromise(
    async () => {
      if (!!form.values.province) {
        const res = await WardService.find({
          where: {
            province: {
              equals: form.values.province,
            },
          },
          page: 1,
          limit: 200,
        });
        return get(res, "data", []);
      }
      return [];
    },
    null,
    [form.values.province]
  );
  assign(ref.current, { form, pagination });
  // Ghi page ra URL bằng replaceState (không tạo lịch sử, không re-render route).
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const cur = url.searchParams.get("page") || "1";
    const next = String(pagination.page || 1);
    if (cur === next) return;
    if (next === "1") url.searchParams.delete("page");
    else url.searchParams.set("page", next);
    window.history.replaceState(window.history.state, "", url.toString());
  }, [pagination.page]);
  assign(form, {
    selectOptions: {
      categories: get(pageService, "categories", []),
      subShopCategories: get(pageService, "subCategories", []),
      provinces: get(pageService, "provinces", []),
      wards: wards || [],
    },
  });
  const handler = {
    onChangeSearch: (searchTerm: string) => {
      ref.current.form.setFieldValue("searchTerm", searchTerm);
      ref.current.pagination.onSearch("searchTerm", searchTerm);
    },
    onShopClick: (item: any) => {
      router.push(`/${item?.slug}`);
    },
    onChangeFilter: (values: any) => {
      // Đổi tỉnh → phường cũ không còn thuộc tỉnh mới, phải bỏ.
      const next = { ...form.values, ...values };
      if (
        "province" in values &&
        values.province !== form.values.province &&
        !("ward" in values && values.ward)
      ) {
        next.ward = "";
      }
      ref.current.form.setValues(next);
      ref.current.pagination.submit(next);
    },
  };
  React.useEffect(() => {
    ref.current.first = false;
    form.handleSubmit();
  }, []);
  return (
    <main className="relative flex flex-col min-h-screen">
      <FormProvider form={form}>
        <Header type="search" />
        <h1 className="sr-only">{t("search.heading")}</h1>
        <Filter
          onChangeSearch={handler.onChangeSearch}
          onChangeFilter={handler.onChangeFilter}
          parentForm={form}
        />
        <div className="flex-1 bg-white flex flex-col">
          <SectionVertical
            title={"Cửa hàng"}
            description={"Khám phá các cửa hàng trên nền tảng Lookme"}
            pagination={pagination}
            onShopClick={handler.onShopClick}
            searchTerm={get(form, "values.searchTerm") || ""}
          />
          <div className="mt-auto">
            <SectionDownloadApp />
          </div>
        </div>
        <Footer />
      </FormProvider>
    </main>
  );
};

export default Comp;
